// /pricing (pages/pricing.js) and the plan card / plan button
// (components/pricing/PricingCard.js, whose PlanCTA the upgrade modal also
// uses). The page renders English on the server and switches on the client.
// See messages/README.md for how to add strings.
export const pricing = {
  en: {
    "pricing.promo": "Your first month from {price}",
    "pricing.title": "Make covers with any voice",
    "pricing.subtitle":
      "Unlimited covers, downloads and all 93,000+ voices. Cancel any time.",
    "pricing.trialLinkInvalid":
      "This free trial link is for a different account or has expired — regular checkout shown.",

    "pricing.basic.subtitle": "For making covers",
    "pricing.creator.subtitle": "For videos and your own voices",
    "pricing.power.subtitle": "For heavy use and developers",
    "pricing.badge.popular": "Most popular",

    "pricing.feature.unlimitedCovers": "Unlimited covers",
    "pricing.feature.download": "Download cover, vocals and music",
    "pricing.feature.library": "The full voice library",
    "pricing.feature.tts": "Text to speech",
    "pricing.feature.duets": "Duets",
    "pricing.feature.credits": "Credits never expire",
    "pricing.feature.train": "Train unlimited voices",
    "pricing.feature.videos": "Shareable videos: Marble Run and lyrics",
    "pricing.feature.coins50": "50 video coins every month",
    "pricing.feature.priority": "Priority processing, 250 per 12 hours",
    "pricing.feature.coinsUnlimited": "Unlimited video coins",
    "pricing.feature.api": "$25 of API credits every month",

    "pricing.compareAll": "Compare everything",
    "pricing.trust.cancel": "Cancel any time, keep what you made",
    "pricing.trust.stripe": "Secure checkout with Stripe",
    "pricing.trust.currency": "Prices in your currency",

    "pricing.compare.title": "Compare plans",
    "pricing.compare.noPlan": "No plan",
    "pricing.compare.browse": "Browse and hear every voice",
    "pricing.compare.covers": "Covers",
    "pricing.compare.downloads": "Downloads",
    "pricing.compare.ttsDuets": "Text to speech and duets",
    "pricing.compare.song": "Generate a song with AI",
    "pricing.compare.train": "Train your own voices",
    "pricing.compare.videos": "Videos (Marble Run, lyrics)",
    "pricing.compare.priority": "Priority processing",
    "pricing.compare.api": "API credits",
    "pricing.compare.unlimited": "Unlimited",
    "pricing.compare.coins50": "50 coins a month",
    "pricing.compare.priorityLimit": "250 per 12 hours",
    "pricing.compare.apiAmount": "$25 a month",

    "pricing.faq.title": "Questions",
    "pricing.faq.noPlan.q": "What can I do without a plan?",
    "pricing.faq.noPlan.a":
      "You can browse and listen to every voice on Jammable. Making covers, downloading them, text to speech and duets all need a plan.",
    "pricing.faq.basic.q": "What is included in the Basic plan?",
    "pricing.faq.basic.a":
      "The Basic plan includes unlimited covers, downloads, access to the full Jammable voice library, text to speech, duets, and your credits never expire.",
    "pricing.faq.creator.q": "How does the Creator plan differ from Basic?",
    "pricing.faq.creator.a":
      "The Creator plan offers everything in Basic plus the ability to train unlimited voices, create shareable videos, and 50 Marble Run coins per month.",
    "pricing.faq.power.q": "What makes the Power plan special?",
    "pricing.faq.power.a":
      "Power includes everything in Creator, plus priority processing (250/12hrs), unlimited Marble Run coins and $25 of API credits every month for professional creators.",
    "pricing.faq.cancel.q": "Can I cancel my subscription anytime?",
    "pricing.faq.cancel.a":
      "Yes, cancel at any time. Your benefits stay active until the end of your billing period.",
    "pricing.faq.change.q": "Can I upgrade or downgrade?",
    "pricing.faq.change.a":
      "Absolutely. Upgrade or downgrade whenever you like and your benefits update accordingly.",

    // components/pricing/PricingCard.js
    "pricing.card.perYear": "a year, about {price} a month",
    "pricing.card.firstMonthThen": "first month, then {price} a month",
    "pricing.card.perMonth": "a month",
    "pricing.card.startFor": "Start for {price}",
    "pricing.card.everythingIn": "Everything in {plan}, plus",
    "pricing.cta.get": "Get {plan}",
    "pricing.cta.yourPlan": "Your plan",
    "pricing.cta.manage": "Manage plan",
    "pricing.cta.upgradeTo": "Upgrade to {plan}",
    "pricing.cta.updated": "Your subscription has been updated!",
    "pricing.error.generic": "Something went wrong. Please contact support!",
  },
  es: {
    "pricing.promo": "Tu primer mes desde {price}",
    "pricing.title": "Haz covers con cualquier voz",
    "pricing.subtitle":
      "Covers ilimitados, descargas y más de 93 mil voces. Cancela cuando quieras.",
    "pricing.trialLinkInvalid":
      "Este enlace de prueba gratis es de otra cuenta o ya venció. Te mostramos el pago normal.",

    "pricing.basic.subtitle": "Para hacer covers",
    "pricing.creator.subtitle": "Para videos y tus propias voces",
    "pricing.power.subtitle": "Para uso intensivo y desarrolladores",
    "pricing.badge.popular": "El más popular",

    "pricing.feature.unlimitedCovers": "Covers ilimitados",
    "pricing.feature.download": "Descarga el cover, la voz y la música",
    "pricing.feature.library": "Toda la biblioteca de voces",
    "pricing.feature.tts": "Texto a voz",
    "pricing.feature.duets": "Duetos",
    "pricing.feature.credits": "Tus créditos no vencen",
    "pricing.feature.train": "Entrena voces ilimitadas",
    "pricing.feature.videos": "Videos para compartir: Marble Run y letras",
    "pricing.feature.coins50": "50 monedas de video al mes",
    "pricing.feature.priority": "Procesamiento prioritario, 250 cada 12 horas",
    "pricing.feature.coinsUnlimited": "Monedas de video ilimitadas",
    "pricing.feature.api": "$25 en créditos de API al mes",

    "pricing.compareAll": "Comparar todo",
    "pricing.trust.cancel": "Cancela cuando quieras y conserva lo que hiciste",
    "pricing.trust.stripe": "Pago seguro con Stripe",
    "pricing.trust.currency": "Precios en tu moneda",

    "pricing.compare.title": "Compara los planes",
    "pricing.compare.noPlan": "Sin plan",
    "pricing.compare.browse": "Explora y escucha todas las voces",
    "pricing.compare.covers": "Covers",
    "pricing.compare.downloads": "Descargas",
    "pricing.compare.ttsDuets": "Texto a voz y duetos",
    "pricing.compare.song": "Genera una canción con IA",
    "pricing.compare.train": "Entrena tus propias voces",
    "pricing.compare.videos": "Videos (Marble Run, letras)",
    "pricing.compare.priority": "Procesamiento prioritario",
    "pricing.compare.api": "Créditos de API",
    "pricing.compare.unlimited": "Sin límite",
    "pricing.compare.coins50": "50 monedas al mes",
    "pricing.compare.priorityLimit": "250 cada 12 horas",
    "pricing.compare.apiAmount": "$25 al mes",

    "pricing.faq.title": "Preguntas",
    "pricing.faq.noPlan.q": "¿Qué puedo hacer sin un plan?",
    "pricing.faq.noPlan.a":
      "Puedes explorar y escuchar todas las voces de Jammable. Para hacer covers, descargarlos, usar texto a voz y duetos necesitas un plan.",
    "pricing.faq.basic.q": "¿Qué incluye el plan Basic?",
    "pricing.faq.basic.a":
      "El plan Basic incluye covers ilimitados, descargas, acceso a toda la biblioteca de voces de Jammable, texto a voz y duetos, y tus créditos no vencen.",
    "pricing.faq.creator.q": "¿En qué se diferencia el plan Creator del Basic?",
    "pricing.faq.creator.a":
      "El plan Creator incluye todo lo de Basic, y además puedes entrenar voces ilimitadas, crear videos para compartir y recibir 50 monedas de Marble Run al mes.",
    "pricing.faq.power.q": "¿Qué tiene de especial el plan Power?",
    "pricing.faq.power.a":
      "Power incluye todo lo de Creator, más procesamiento prioritario (250 cada 12 h), monedas de Marble Run ilimitadas y $25 en créditos de API cada mes, para creadores profesionales.",
    "pricing.faq.cancel.q": "¿Puedo cancelar mi suscripción cuando quiera?",
    "pricing.faq.cancel.a":
      "Sí, cancela cuando quieras. Tus beneficios siguen activos hasta el final de tu periodo de facturación.",
    "pricing.faq.change.q": "¿Puedo cambiar a un plan superior o inferior?",
    "pricing.faq.change.a":
      "Claro. Cambia de plan cuando quieras y tus beneficios se ajustan al nuevo plan.",

    "pricing.card.perYear": "al año, unos {price} al mes",
    "pricing.card.firstMonthThen": "el primer mes, luego {price} al mes",
    "pricing.card.perMonth": "al mes",
    "pricing.card.startFor": "Empieza por {price}",
    "pricing.card.everythingIn": "Todo lo de {plan}, y además",
    "pricing.cta.get": "Obtener {plan}",
    "pricing.cta.yourPlan": "Tu plan",
    "pricing.cta.manage": "Gestionar plan",
    "pricing.cta.upgradeTo": "Pasar a {plan}",
    "pricing.cta.updated": "¡Tu suscripción se actualizó!",
    "pricing.error.generic": "Algo salió mal. Contacta a soporte.",
  },
  pt: {
    "pricing.promo": "Seu primeiro mês a partir de {price}",
    "pricing.title": "Faça covers com qualquer voz",
    "pricing.subtitle":
      "Covers ilimitados, downloads e mais de 93 mil vozes. Cancele quando quiser.",
    "pricing.trialLinkInvalid":
      "Este link de teste grátis é de outra conta ou expirou. Mostramos o pagamento normal.",

    "pricing.basic.subtitle": "Para fazer covers",
    "pricing.creator.subtitle": "Para vídeos e suas próprias vozes",
    "pricing.power.subtitle": "Para uso intenso e desenvolvedores",
    "pricing.badge.popular": "Mais popular",

    "pricing.feature.unlimitedCovers": "Covers ilimitados",
    "pricing.feature.download": "Baixe o cover, os vocais e a música",
    "pricing.feature.library": "Toda a biblioteca de vozes",
    "pricing.feature.tts": "Texto para fala",
    "pricing.feature.duets": "Duetos",
    "pricing.feature.credits": "Os créditos não expiram",
    "pricing.feature.train": "Treine vozes ilimitadas",
    "pricing.feature.videos": "Vídeos para compartilhar: Marble Run e letras",
    "pricing.feature.coins50": "50 moedas de vídeo por mês",
    "pricing.feature.priority":
      "Processamento prioritário, 250 a cada 12 horas",
    "pricing.feature.coinsUnlimited": "Moedas de vídeo ilimitadas",
    "pricing.feature.api": "$25 em créditos de API por mês",

    "pricing.compareAll": "Comparar tudo",
    "pricing.trust.cancel": "Cancele quando quiser e fique com o que criou",
    "pricing.trust.stripe": "Pagamento seguro com Stripe",
    "pricing.trust.currency": "Preços na sua moeda",

    "pricing.compare.title": "Compare os planos",
    "pricing.compare.noPlan": "Sem plano",
    "pricing.compare.browse": "Explore e ouça todas as vozes",
    "pricing.compare.covers": "Covers",
    "pricing.compare.downloads": "Downloads",
    "pricing.compare.ttsDuets": "Texto para fala e duetos",
    "pricing.compare.song": "Gere uma música com IA",
    "pricing.compare.train": "Treine suas próprias vozes",
    "pricing.compare.videos": "Vídeos (Marble Run, letras)",
    "pricing.compare.priority": "Processamento prioritário",
    "pricing.compare.api": "Créditos de API",
    "pricing.compare.unlimited": "Sem limite",
    "pricing.compare.coins50": "50 moedas por mês",
    "pricing.compare.priorityLimit": "250 a cada 12 horas",
    "pricing.compare.apiAmount": "$25 por mês",

    "pricing.faq.title": "Perguntas",
    "pricing.faq.noPlan.q": "O que posso fazer sem um plano?",
    "pricing.faq.noPlan.a":
      "Você pode explorar e ouvir todas as vozes do Jammable. Para fazer covers, baixá-los, usar texto para fala e duetos, você precisa de um plano.",
    "pricing.faq.basic.q": "O que está incluído no plano Basic?",
    "pricing.faq.basic.a":
      "O plano Basic inclui covers ilimitados, downloads, acesso a toda a biblioteca de vozes do Jammable, texto para fala e duetos, e seus créditos não expiram.",
    "pricing.faq.creator.q":
      "Qual a diferença entre o plano Creator e o Basic?",
    "pricing.faq.creator.a":
      "O plano Creator inclui tudo do Basic e ainda permite treinar vozes ilimitadas, criar vídeos para compartilhar e receber 50 moedas de Marble Run por mês.",
    "pricing.faq.power.q": "O que o plano Power tem de especial?",
    "pricing.faq.power.a":
      "O Power inclui tudo do Creator, mais processamento prioritário (250 a cada 12 h), moedas de Marble Run ilimitadas e $25 em créditos de API por mês, para criadores profissionais.",
    "pricing.faq.cancel.q":
      "Posso cancelar minha assinatura a qualquer momento?",
    "pricing.faq.cancel.a":
      "Sim, cancele quando quiser. Seus benefícios continuam ativos até o fim do período de cobrança.",
    "pricing.faq.change.q": "Posso mudar para um plano maior ou menor?",
    "pricing.faq.change.a":
      "Claro. Mude de plano quando quiser e seus benefícios se ajustam ao novo plano.",

    "pricing.card.perYear": "por ano, cerca de {price} por mês",
    "pricing.card.firstMonthThen": "no primeiro mês, depois {price} por mês",
    "pricing.card.perMonth": "por mês",
    "pricing.card.startFor": "Comece por {price}",
    "pricing.card.everythingIn": "Tudo do {plan}, e mais",
    "pricing.cta.get": "Assinar o {plan}",
    "pricing.cta.yourPlan": "Seu plano",
    "pricing.cta.manage": "Gerenciar plano",
    "pricing.cta.upgradeTo": "Mudar para o {plan}",
    "pricing.cta.updated": "Sua assinatura foi atualizada!",
    "pricing.error.generic": "Algo deu errado. Fale com o suporte.",
  },
};
